/**
 * Sending somebody to another panel on the same screen.
 *
 * Lifted out of `Objectives.tsx` when the research screen needed the same jump (F-129): a
 * subject that found nothing offers the page from the learner's own book as one of its two
 * remedies, and that upload control is `Sources`, several panels below. A second copy of this
 * function would have been three lines and would have drifted the first time one of them was
 * fixed — this one has already been fixed twice, once for focus and once for a collapsed
 * `<details>`, and both fixes are the comments below.
 *
 * It is not in the test-compiled set and cannot be: it is nothing but DOM.
 */
import { MATERIAL_PANEL_ID } from './Sources';

/**
 * The research section of the objective view, for the two places that used to link to it.
 *
 * **It was a route and is now a section** (F-141), and that is the whole reason this constant
 * exists. `Curriculum` offered *"see every subject"* and the build offer *"see everything that
 * was read"*, and both were links to `/research` — one of them written as the literal
 * `/investigacion`, which F-142 had already retired and which therefore reached the section
 * through a redirect. There is no other screen to link to now, so both become the same jump the
 * uploader already gets: open the section, scroll to it, move focus into it.
 *
 * It names the `Disclosure` itself rather than a panel inside it, which is why `goToPanel` below
 * had to learn about both shapes. The alternative was a wrapper element around `Research`, and
 * that one is worse than it looks: `.disclosure-body > .panel` in `styles.css` flattens a wrapped
 * component's card so it is not a bordered box inside a bordered box, and a wrapper breaks the
 * child combinator silently — every panel in the section would grow a second border and nothing
 * anywhere would report it.
 *
 * It is also the section's storage key and its URL fragment, so `/plan#research` and this jump
 * arrive at the same place by construction.
 */
export const RESEARCH_PANEL_ID = 'research';

/**
 * Scrolls and moves focus.
 *
 * Not exported. Its callers are all in this file, and an exported helper that takes an arbitrary
 * id is surface nobody is maintaining — two named jumps are two things a reader can find, where
 * `goToPanel('whatever')` is a call anybody can write against an element that may not exist.
 *
 * Focus moves with the scroll deliberately. A button that only moves the viewport leaves a
 * keyboard or screen-reader user exactly where they were, having apparently pressed something
 * that did nothing.
 */
function goToPanel(id: string): void {
  const panel = document.getElementById(id);
  if (!panel) return;
  /*
   * The section is opened first, and without this the button silently does nothing.
   *
   * A closed `<details>` hides its contents with `display: none`, and an element that is not
   * rendered cannot be scrolled to and cannot take focus — both calls below are accepted and
   * discarded. Sections fold now, and the learner's choice is remembered, so "add a page"
   * pressed by somebody who collapsed the uploader last week would be a button that does nothing
   * at all with nothing anywhere saying why.
   *
   * Setting the attribute is enough to keep React in step: the browser fires `toggle`, which is
   * the same event a tap on the summary fires, so `Disclosure` records the section as open
   * exactly as it would have.
   *
   * **Two shapes, because the two targets are different elements** (F-141). The uploader is a
   * `.panel` *inside* a disclosure, so its `<details>` is an ancestor; the research section *is*
   * the disclosure, so its `<details>` is a child. `closest` answers the first and nothing at all
   * for the second, which would be a button that scrolls to a section it forgot to open.
   */
  const details = panel.closest('details') ?? panel.querySelector('details');
  details?.setAttribute('open', '');
  panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  /*
   * Focus moves to the target itself when it can take focus, and to the section's own summary
   * when it cannot.
   *
   * A `<section>` with no `tabindex` is not focusable, and `focus()` on one is accepted and
   * discarded — which is the same silent nothing this whole function exists to avoid. The
   * summary is the right landing anyway: it is the control that folds the section, and it is
   * what `Disclosure`'s own fragment link focuses.
   */
  const landing = panel.hasAttribute('tabindex')
    ? panel
    : panel.querySelector<HTMLElement>('summary');
  landing?.focus({ preventScroll: true });
}

/**
 * Sends the learner to the uploader instead of putting a second file control beside whatever
 * asked for a page.
 *
 * `Sources` owns presigning, the content-type fallback for phone browsers that report none, and
 * the HEIC rule that is the only reason photographing a page from an iPhone works at all. A
 * second upload button beside the gaps — or beside a barren subject — would be a second copy of
 * all of it, and the copies would drift the first time one of them was fixed.
 */
export function goToUpload(): void {
  goToPanel(MATERIAL_PANEL_ID);
}

/**
 * Sends the learner to what was actually read, which is a section of this screen and not a
 * screen of its own (F-141).
 *
 * **A jump rather than a `Link` to a fragment, and the difference is a second press.** A link to
 * `/plan#research` navigates when the learner is elsewhere and does nothing at all when they are
 * already on `/plan` with that fragment in the bar — React Router does not re-fire a location
 * that has not changed, so `Disclosure`'s hash effect never runs a second time. Both call sites
 * are on this screen, so both are always in exactly that case.
 */
export function goToResearch(): void {
  goToPanel(RESEARCH_PANEL_ID);
}
